// src/services/api.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';

// Helper to construct image URL
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_URL.replace('/api', '')}/uploads/${path}`;
};

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to attach auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}, (error) => {
  return Promise.reject(error);
});

export const fieldService = {
  getFields: async () => {
    const response = await api.get('/fields');
    return response.data;
  },

  createField: async (fieldData) => {
    const response = await api.post('/fields', fieldData);
    return response.data;
  },

  updateField: async (id, fieldData) => {
    const response = await api.put(`/fields/${id}`, fieldData);
    return response.data;
  },

  deleteField: async (id) => {
    const response = await api.delete(`/fields/${id}`);
    return response.data;
  },

  getNdviTimeline: async (fieldId) => {
    const response = await api.get(`/fields/${fieldId}/timeline`);
    return response.data;
  },

  getAnalyzedItemIds: async (fieldId) => {
    const response = await api.get(`/fields/${fieldId}/analyzed-items`);
    return response.data;
  }
};

export const imageService = {
  // 1. Adım: Görüntü Arama
  searchSentinelImages: async (fieldId, startDate = null) => {
    // Default to strict 1 year ago if not provided
    const end = new Date();
    const start = startDate ? new Date(startDate) : new Date();
    if (!startDate) start.setFullYear(start.getFullYear() - 1);

    const response = await api.post('/images/search', {
      fieldId,
      startDate: start.toISOString(),
      endDate: end.toISOString(),
      maxCloudCover: 0.3 // 30%
    });

    return response.data.features.map(f => ({
      id: f.id, // GEE ID
      date: f.properties.datetime,
      cloudCover: f.properties['eo:cloud_cover'],
      platform: f.properties.platform,
      thumbnail: f.assets.thumbnail.href
    }));
  },

  // Helper function to group NDVI results by date and average values
  groupByDateAndAverage: (results) => {
    const grouped = {};

    results.forEach(result => {
      // Extract just the date part (YYYY-MM-DD)
      const dateKey = new Date(result.date).toISOString().split('T')[0];

      if (!grouped[dateKey]) {
        grouped[dateKey] = {
          items: [],
          date: dateKey
        };
      }

      grouped[dateKey].items.push(result);
    });

    // Calculate averages for each date
    return Object.values(grouped).map(group => {
      const items = group.items;
      const avgNdvi = items.reduce((sum, item) => sum + item.meanNdvi, 0) / items.length;
      const avgMin = items.reduce((sum, item) => sum + item.minNdvi, 0) / items.length;
      const avgMax = items.reduce((sum, item) => sum + item.maxNdvi, 0) / items.length;
      const avgStd = items.reduce((sum, item) => sum + item.stdNdvi, 0) / items.length;

      const avgNdre = items.reduce((sum, item) => sum + (item.meanNdre || 0), 0) / items.length;
      const avgMinNdre = items.reduce((sum, item) => sum + (item.minNdre || 0), 0) / items.length;
      const avgMaxNdre = items.reduce((sum, item) => sum + (item.maxNdre || 0), 0) / items.length;
      const avgStdNdre = items.reduce((sum, item) => sum + (item.stdNdre || 0), 0) / items.length;

      const avgNdmi = items.reduce((sum, item) => sum + (item.meanNdmi || 0), 0) / items.length;
      const avgMinNdmi = items.reduce((sum, item) => sum + (item.minNdmi || 0), 0) / items.length;
      const avgMaxNdmi = items.reduce((sum, item) => sum + (item.maxNdmi || 0), 0) / items.length;
      const avgStdNdmi = items.reduce((sum, item) => sum + (item.stdNdmi || 0), 0) / items.length;

      const avgCloud = items.reduce((sum, item) => sum + item.cloudCover, 0) / items.length;

      // Use the image with lowest cloud cover
      const bestItem = items.reduce((best, item) =>
        item.cloudCover < best.cloudCover ? item : best
      );

      return {
        id: bestItem.id,
        date: group.date,
        meanNdvi: isNaN(avgNdvi) ? 0 : avgNdvi,
        minNdvi: isNaN(avgMin) ? 0 : avgMin,
        maxNdvi: isNaN(avgMax) ? 0 : avgMax,
        stdNdvi: isNaN(avgStd) ? 0 : avgStd,
        ndviImagePath: bestItem.ndviImagePath,

        meanNdre: isNaN(avgNdre) ? 0 : avgNdre,
        minNdre: isNaN(avgMinNdre) ? 0 : avgMinNdre,
        maxNdre: isNaN(avgMaxNdre) ? 0 : avgMaxNdre,
        stdNdre: isNaN(avgStdNdre) ? 0 : avgStdNdre,
        ndreImagePath: bestItem.ndreImagePath,

        meanNdmi: isNaN(avgNdmi) ? 0 : avgNdmi,
        minNdmi: isNaN(avgMinNdmi) ? 0 : avgMinNdmi,
        maxNdmi: isNaN(avgMaxNdmi) ? 0 : avgMaxNdmi,
        stdNdmi: isNaN(avgStdNdmi) ? 0 : avgStdNdmi,
        ndmiImagePath: bestItem.ndmiImagePath,

        cloudCover: isNaN(avgCloud) ? 0 : avgCloud,
        zonalStats: bestItem.zonalStats,
        imageCount: items.length
      };
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  },

  // 2. Adım: NDVI Analizi
  analyzeImages: async (images, fieldId, force = false) => {
    // Batch processing to avoid overwhelming the server
    const results = [];
    const BATCH_SIZE = 3;

    for (let i = 0; i < images.length; i += BATCH_SIZE) {
      console.log(`Processing batch ${Math.floor(i / BATCH_SIZE) + 1} of ${Math.ceil(images.length / BATCH_SIZE)}`);
      const batch = images.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(img =>
        api.post(`/images/analyze/${encodeURIComponent(img.id)}${force ? '?force=true' : ''}`, {
          fieldId,
          itemId: img.id,
          acquisitionDate: img.date,
          cloudCover: img.cloudCover
        }, {
          timeout: 120000 // 2 minutes timeout for GEE processing
        }).then(res => {
          const data = res.data;
          return {
            id: data.id,
            date: data.acquisitionDate,
            meanNdvi: data.meanNdvi,
            minNdvi: data.minNdvi,
            maxNdvi: data.maxNdvi,
            stdNdvi: data.stdNdvi,
            ndviImagePath: getImageUrl(data.ndviImagePath),

            meanNdre: data.meanNdre,
            minNdre: data.minNdre,
            maxNdre: data.maxNdre,
            stdNdre: data.stdNdre,
            ndreImagePath: getImageUrl(data.ndreImagePath),

            meanNdmi: data.meanNdmi,
            minNdmi: data.minNdmi,
            maxNdmi: data.maxNdmi,
            stdNdmi: data.stdNdmi,
            ndmiImagePath: getImageUrl(data.ndmiImagePath),

            cloudCover: data.cloudCover,
            zonalStats: data.zonalStats
          };
        }).catch(err => {
          const errorMsg = err.response?.data?.details || err.message;
          console.error(`Failed to analyze image ${img.id}: ${errorMsg}`, err);
          return null;
        })
      );

      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);

      // Add a small delay between batches to avoid overwhelming the server/GEE
      if (i + BATCH_SIZE < images.length) {
        await new Promise(r => setTimeout(r, 1000));
      }
    }

    const filteredResults = results.filter(r => r !== null);

    // Group by date and average values for same-day images
    return imageService.groupByDateAndAverage(filteredResults);
  }
};

export const userService = {
  getAllUsers: async () => {
    const response = await api.get('/users');
    return response.data;
  },

  approveUser: async (userId) => {
    const response = await api.put(`/users/${userId}/approve`);
    return response.data;
  },

  rejectUser: async (userId) => {
    const response = await api.put(`/users/${userId}/reject`);
    return response.data;
  },

  updateUserRole: async (userId, role) => {
    const response = await api.put(`/users/${userId}/role`, { role });
    return response.data;
  },

  updateUserStatus: async (userId, status) => {
    const response = await api.put(`/users/${userId}/status`, { status });
    return response.data;
  },

  deleteUser: async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
  },

  updateProfile: async (profileData) => {
    const response = await api.put('/users/profile', profileData);
    return response.data;
  },
  getProfile: async () => {
    const response = await api.get('/users/profile');
    return response.data;
  }
};

export const messageService = {
  sendMessage: async (messageData) => {
    const response = await api.post('/messages', messageData);
    return response.data;
  },
  getConversation: async (otherUserId) => {
    const response = await api.get(`/messages/conversation/${otherUserId}`);
    return response.data;
  },
  getMessagingUsers: async () => {
    const response = await api.get('/messages/users');
    return response.data;
  },
  getAdmin: async () => {
    const response = await api.get('/messages/admin');
    return response.data;
  },
  getUnreadSummary: async () => {
    const response = await api.get('/messages/total-unread');
    return response.data;
  },
  sendBroadcast: async (content) => {
    const response = await api.post('/messages/broadcast', { content });
    return response.data;
  }
};

export const aiService = {
  analyze: async (field, analysisData, language = 'tr') => {
    const response = await api.post('/ai/analyze', { field, analysisData, language });
    return response.data;
  },
  getStatus: async () => {
    const response = await api.get('/ai/status');
    return response.data;
  },
  getSupportedCrops: async () => {
    const response = await api.get('/ai/supported-crops');
    return response.data;
  }
};

// Export helper for use in components
export { getImageUrl };
